import { api, HttpError } from "@/lib/http";
import type { User } from "@/types/api";
import { useEffect } from "react";
import { create } from "zustand";

interface AuthState {
  user: User | null;
  status: "unknown" | "authenticated" | "anonymous";
  setUser: (u: User | null) => void;
  setStatus: (s: AuthState["status"]) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: "unknown",
  setUser: (user) => set({ user, status: user ? "authenticated" : "anonymous" }),
  setStatus: (status) => set({ status }),
}));

export function useHydrateSession() {
  const { status, setUser, setStatus } = useAuthStore();
  useEffect(() => {
    if (status !== "unknown") return;
    let cancelled = false;
    api
      .get<User>("/api/me")
      .then((me) => !cancelled && setUser(me))
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 401) setStatus("anonymous");
        else setStatus("anonymous");
      });
    return () => {
      cancelled = true;
    };
  }, [status, setUser, setStatus]);
}

export async function login(email: string, password: string) {
  const user = await api.post<User>("/auth/login", { email, password });
  useAuthStore.getState().setUser(user);
  return user;
}

export async function logout() {
  try {
    await api.post("/auth/logout");
  } finally {
    useAuthStore.getState().setUser(null);
  }
}
